import set from 'lodash.set';
import mergeWith from 'lodash.mergewith';
import { Steps } from 'src/context/salesforce-context';
import { Hint, PageUpdates } from 'src/features/common/types/application/pending-app';
import { PageName } from '../pages/page-names';

export type FormStatus = {
  initialDisabled: { [name: string]: boolean };
  initialHint: { [name: string]: Hint[] };
};

const getUpdates = (pageUpdates: PageUpdates[] | undefined, pageName: string) =>
  pageUpdates?.find(({ subStepName }) => subStepName.toLowerCase() === pageName.toLowerCase());

const allDisabled = (allFields: { [field: string]: any }) =>
  Object.keys(allFields).reduce((accum, curr) => {
    if (Array.isArray(allFields[curr])) {
      accum[curr] = allFields[curr].map((currentField: { [field: string]: any }) =>
        Object.keys(currentField).reduce((disabledObj, field) => {
          disabledObj[field] = true;
          return disabledObj;
        }, {}),
      );

      return accum;
    }

    if (typeof allFields[curr] === 'object' && allFields[curr] !== null) {
      accum[curr] = Object.keys(allFields[curr]).reduce((acc, curr) => {
        acc[curr] = true;
        return acc;
      }, {});

      return accum;
    }

    accum[curr] = true;
    return accum;
  }, {});

export const merger = (first, second) => {
  if (typeof first === 'object') {
    return mergeWith(first, second, merger);
  }

  return first && second;
};

export const getFormStatus = (
  pageUpdates: PageUpdates[] | undefined,
  allFields: { [field: string]: any },
  pageName: string,
): FormStatus => {
  const updates: PageUpdates | undefined = getUpdates(pageUpdates, pageName);

  if (!updates) {
    throw new Error(
      `Form status: Attempting to fetch page updates for a page that has no required updates. Page: ${pageName}`,
    );
  }

  return updates.fields.reduce<FormStatus>(
    (accum, field) => {
      set(accum.initialDisabled, field.fieldName, false);
      set(accum.initialHint, field.fieldName, field.hints);

      return accum;
    },
    { initialDisabled: allDisabled(allFields), initialHint: {} } as FormStatus,
  );
};

export const getDirectorsFormStatus = (
  pageUpdates: PageUpdates[] | undefined,
  fields: { directors: any[] },
  isLegacy: boolean = false,
) => {
  const pageName = isLegacy ? Steps.BUSINESS_IDS : PageName.DIRECTOR_DETAILS;
  const updates: PageUpdates = getUpdates(pageUpdates, pageName) || { fields: [], subStepName: '', uiExplanation: '', categoryName: '', stepName: '' };

  return updates.fields.reduce<FormStatus>(
    (accum, field) => {
      field.hints.forEach((hint) => {
        const idx = fields.directors.findIndex((director) => director.idNumber === hint.id);

        if (idx || idx === 0) {
          set(accum.initialDisabled, `directors[${idx}].${field.fieldName}`, false);
          set(accum.initialHint, `directors[${idx}].${field.fieldName}`, field.hints);
        }
      });

      return accum;
    },
    { initialDisabled: allDisabled(fields), initialHint: {} } as FormStatus,
  );
};

type atom = string | boolean | number;

// For Non-credit vetting business id's page LEGACY
export const getDirectorsFormStatusNoCV = (
  pageUpdates: PageUpdates[],
  fields: { [key: string]: atom[] | atom },
) => {
  const updates: PageUpdates | undefined = getUpdates(pageUpdates, Steps.BUSINESS_IDS);

  if (!updates) {
    throw new Error(
      `Form status: Attempting to fetch page updates for a page that has no required updates. Page ${Steps.BUSINESS_IDS}`,
    );
  }

  const initialStatus = Object.entries(fields).reduce(
    (accum, [key, value]) => {
      if (Array.isArray(value)) {
        accum.initialDisabled[key] = value.map(() => true);
        accum.initialHint[key] = value.map(() => []);
        return accum;
      }

      accum.initialDisabled[key] = true;
      accum.initialHint[key] = [];
      return accum;
    },
    { initialDisabled: {}, initialHint: {} },
  );

  return updates.fields.reduce((accum, field) => {
    field.hints.forEach((hint) => {
      const matchingIndex =
        Array.isArray(fields.idNumbers) && fields.idNumbers.findIndex((id) => hint.id === id);

      if (matchingIndex || matchingIndex === 0) {
        const key = `idNumbers.${matchingIndex}`;
        set(accum.initialDisabled, key, false);
        set(accum.initialHint, key, field.hints);
      }
    });

    return accum;
  }, initialStatus);
};
